import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useOperatorAuth } from '../context/OperatorAuthContext';
import { ApiError } from '../api/client';

const LoginPage: React.FC = () => {
  const { login, api } = useOperatorAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state ?? {}) as { notice?: string; forgot?: boolean };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Forgot-password: one email field, and whatever the server answers. The
  // server's 202 body is the same for every address, so it is shown as-is.
  const [forgotOpen, setForgotOpen] = useState(Boolean(navState.forgot));
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotResult, setForgotResult] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const onForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError(null);
    setForgotResult(null);
    setForgotSending(true);
    try {
      const res = await api.forgotPassword(email);
      setForgotResult(res.message);
    } catch (err) {
      setForgotError(err instanceof ApiError ? err.message : 'Could not send the reset request.');
    } finally {
      setForgotSending(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/console', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Login failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-card">
        <h1>Stoa — Operator login</h1>
        <p className="auth-sub">Sign in to the AEXgora console.</p>
        {navState.notice && <p className="auth-notice" role="status">{navState.notice}</p>}
        <form onSubmit={onSubmit} className="auth-form">
          <label>
            <span>Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          {error && <div className="auth-error" role="alert">{error}</div>}
          <button type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Log in'}
          </button>
        </form>
        <p className="auth-foot">
          <button
            type="button"
            className="link-button"
            aria-expanded={forgotOpen}
            onClick={() => setForgotOpen((o) => !o)}
          >
            Forgot password?
          </button>
        </p>
        {forgotOpen && (
          <form onSubmit={onForgot} className="auth-form forgot-form" aria-label="Reset password">
            <p className="auth-hint">
              Enter the account's email. If it has an operator account, a reset link is sent to it.
            </p>
            <label>
              <span>Account email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            {forgotResult && <p className="auth-notice" role="status">{forgotResult}</p>}
            {forgotError && <div className="auth-error" role="alert">{forgotError}</div>}
            <button type="submit" disabled={forgotSending}>
              {forgotSending ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}
        <p className="auth-foot">
          Need an account? <Link to="/register">Register</Link>
        </p>
      </div>
    </main>
  );
};

export default LoginPage;
